import React, { useEffect, useState } from "react";
import {
  Stack,
  CommandBarButton,
  Spinner,
  Dialog,
  DialogType,
  DialogFooter,
  DefaultButton,
  Text,
  Separator,
} from "@fluentui/react";
import PrimaryButton from "../../../../Forms/PrimaryButton";
import ChecklistActionItemsDragAndDropDeck from "./ChecklistActionItemsDragAndDropDeck";
import { useActionItemLabelLookups } from "../../ActionItems/ActionItemLabel";
import { isChecklistPublished } from "../../../../../util";

const ChecklistActionItemsContainer = ({
  checklistId,
  putActionItemsLoading,
  putActionItems,
  setIsModalCreateNewModalOpen,
  isWizard,
  actionItems,
  setActionItems,
  checklist,
  getChecklistLoading,
  actionItemsData,
  loadingActionItems,
}) => {
  const [actionItemIdToDelete, setActionItemIdToDelete] = useState();
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const labelLookups = useActionItemLabelLookups();

  const options =
    actionItemsData &&
    actionItemsData.map((actionItem) => ({
      key: actionItem.actionItemID,
      text: actionItem.actionItemTitle,
      actionItem,
    }));

  const filteredOptions =
    actionItemsData &&
    options.filter(
      (option) =>
        !actionItems.some(
          (actionItem) => actionItem.actionItemID === option.key
        )
    );

  const onDragEnd = (result) => {
    const { destination, source } = result;

    if (!destination) {
      return;
    }

    if (
      destination.droppableId === source.droppableId &&
      destination.index === source.index
    ) {
      return;
    }

    const newActionItems = Array.from(actionItems);

    const actionItemRemoved = newActionItems.splice(source.index, 1);
    newActionItems.splice(destination.index, 0, {
      ...actionItemRemoved.find(() => true),
    });
    setActionItems(
      newActionItems.map((actionItem, index) => ({
        ...actionItem,
        orderNumber: index,
      }))
    );
  };

  const handleOnActionItemChange = (item, currentActionItem) => {
    setActionItems((prevActionItems) =>
      prevActionItems.map((actionItem) => {
        if (actionItem.key === currentActionItem.key) {
          return {
            ...actionItem,
            actionItemID: item.key,
            actionItemTitle: options.find((option) => option.key === item.key)
              ?.text,
          };
        }
        return actionItem;
      })
    );
  };

  const handleOnRequirementChange = (event, item, actionItemKey) => {
    setActionItems((prevActionItems) =>
      prevActionItems.map((actionItem) => {
        if (actionItem.key === actionItemKey) {
          return {
            ...actionItem,
            requirement: item.key,
          };
        }
        return actionItem;
      })
    );
  };

    const handleAddNewActionItem = () => {
        if (!filteredOptions || filteredOptions.length === 0) {
            return;
        }

    const defaultRequirement = checklist?.completionRules === "Required Only" ? "Required" : "Not Required";
    setActionItems((prevActionItems, index) => [
      ...prevActionItems,
      {
        key: `actionItem_${Date.now()}`,
        actionItemID: null,
        checklistID: checklistId,
        orderNumber: prevActionItems.length,
        requirement: defaultRequirement,
        validated: true,
      },
    ]);
  };

  const setActionItemToDelete = (actionItemId) => {
    setActionItemIdToDelete(actionItemId);
    setShowDeleteModal(true);
  };

  const handleDelete = (itemKeyOrId) => {
    setActionItems((prevActionItems) =>
      prevActionItems
        .filter((actionItem) => 
          actionItem.key !== itemKeyOrId && actionItem.actionItemID !== itemKeyOrId
        )
        .map((actionItem, index) => ({
          ...actionItem,
          orderNumber: index,
        }))
    );
  };

  useEffect(() => {
    if (checklist && !loadingActionItems) {
      const itemsWithKeys = (checklist.actionItems || []).map((item, index) => ({
        ...item,
        key: item.key || `actionItem_${item.actionItemID || index}`,
      }));
      setActionItems(itemsWithKeys);
    }
  }, [checklist, loadingActionItems]);

  if (putActionItemsLoading) {
    return <Spinner className="absolute-center" label="Saving Changes..." />;
  }

  if (getChecklistLoading) {
    return <Spinner className="absolute-center" label="Loading Checklist..." />;
  }

  if (loadingActionItems) {
    return (
      <Spinner className="absolute-center" label="Loading Action Items..." />
    );
  }
  if (isChecklistPublished(checklist?.status, checklist?.publishedDate)) {
    return (
      <Text>Sorry, you cannot edit a published checklist's action items</Text>
    );
  }
  return (
    <div style={{ width: 770 }}>
      <ChecklistActionItemsCommandBar
        putActionItemsLoading={putActionItemsLoading}
        handleAddNewActionItem={handleAddNewActionItem}
        setIsModalCreateNewModalOpen={setIsModalCreateNewModalOpen}
        putActionItems={putActionItems}
        actionItemsData={actionItemsData}
        isWizard={isWizard}
      />
      <Separator>{checklist && checklist.name}</Separator>
      {actionItems && actionItems.length > 0 ? (
        <ChecklistActionItemsDragAndDropDeck
          actionItems={actionItems}
          onDragEnd={onDragEnd}
          options={options}
          filteredOptions={filteredOptions}
          handleOnActionItemChange={handleOnActionItemChange}
          handleOnRequirementChange={handleOnRequirementChange}
          setActionItemToDelete={setActionItemToDelete}
          checklist={checklist}
          labelLookups={labelLookups}
        />
      ) : (
        <Text>There are no Action Items to display</Text>
      )}
      <RemoveChecklistActionItemModal
        toggleShowModal={() => setShowDeleteModal((prev) => !prev)}
        showModal={showDeleteModal}
        handleDelete={handleDelete}
        actionItemId={actionItemIdToDelete}
      />
    </div>
  );
};

const ChecklistActionItemsCommandBar = ({
  putActionItemsLoading,
  handleAddNewActionItem,
  setIsModalCreateNewModalOpen,
  putActionItems,
  actionItemsData,
  isWizard,
}) => (
  <Stack horizontal styles={{ root: { height: 44 } }}>
    {putActionItemsLoading ? (
      <Spinner label="Saving Changes..." />
    ) : (
      <>
        {!isWizard && (
          <CommandBarButton
            iconProps={{ iconName: "Save" }}
            text="Save"
            onClick={putActionItems}
          />
        )}
        {actionItemsData && actionItemsData.length > 0 && (
          <CommandBarButton
            iconProps={{ iconName: "Add" }}
            text="Add Existing Action Item"
            onClick={handleAddNewActionItem}
          />
        )}
        <CommandBarButton
          iconProps={{ iconName: "DependencyAdd" }}
          text="Create New Action Item"
          onClick={() => setIsModalCreateNewModalOpen()}
        />
      </>
    )}
  </Stack>
);

const RemoveChecklistActionItemModal = ({
  showModal,
  toggleShowModal,
  handleDelete,
  actionItemId,
}) => {
  return (
    <Dialog
      hidden={!showModal}
      onDismiss={toggleShowModal}
      modalProps={{
        isBlocking: true,
      }}
      dialogContentProps={{
        type: DialogType.normal,
        title: "Warning",
        subText: "Are you sure you want to remove this action item?",
      }}
    >
      <DialogFooter>
        <PrimaryButton
          onClick={async () => {
            try {
              await handleDelete(actionItemId);
              toggleShowModal();
            } catch (e) {
              throw e;
            }
          }}
          text="Yes, remove"
        />
        <DefaultButton onClick={toggleShowModal} text="Cancel" />
      </DialogFooter>
    </Dialog>
  );
};
export default ChecklistActionItemsContainer;
